/* Where a sub's SOV line is allocated, as pictures (Duncan 2026-10-29: "something a little more graphically pretty to
   show where it's allocated? Like pie graphs"; the mockup he OK'd): a donut of the line's tasks in its window, and a
   slim bar under each line in the table.
   The donut's colors are the validated categorical palette (the dataviz reference: slots 1-6, stepped for light and
   for dark, checked for colorblind separation): a task's slice and its chip in the list share a slot; past six tasks,
   the five largest keep theirs and the rest fold into one grey "Other". Three of the light steps are faint on the light
   page, so every slice is named in the list beside it. The bar is a meter: one hue, a segment a task, 2px apart; the
   track, what no task has taken. Hover names the task. */
import React from 'react';
import { useHoverTip } from '../ui';
import { moneyOfCents } from '../../lib/subcontractors';

export const VIZ_CSS = `
.sov-viz { --sv-1: #2a78d6; --sv-2: #eb6834; --sv-3: #1baf7a; --sv-4: #eda100; --sv-5: #e87ba4; --sv-6: #008300; --sv-other: #9a988f; --sv-track: #e6e4dc; }
/* Dark: the app's, and the lifted sub card's (subs/Lift.js is portaled outside .app and takes the theme as .theme-dark). */
.app.dark .sov-viz, .theme-dark .sov-viz { --sv-1: #3987e5; --sv-2: #d95926; --sv-3: #199e70; --sv-4: #c98500; --sv-5: #d55181; --sv-6: #008300; --sv-other: #7d7b74; --sv-track: #3a3936; }
`;
const SLOTS = 6;
const shortPlace = (p) => String(p || '').replace(/\s+Elementary School$/i, ' ES').replace(/\s+Middle School$/i, ' MS').replace(/\s+High School$/i, ' HS');
const taskWords = (k) => `${k.task.task_code ? `${k.task.task_code} ` : ''}${k.task.task_name}${k.task.building_area_phase ? ` · ${shortPlace(k.task.building_area_phase)}` : ''}`;

/** The donut's slices: up to six tasks in their slots, past six the five largest and an "Other". [{ key, color, cents,
    links, other }] -- and each link's color by its id, for the list's chips. */
export function slicesOf(links) {
  const slice = (k, i) => ({ key: String(k.id), color: `var(--sv-${i + 1})`, cents: k.allocation_amount_cents, links: [k], other: false });
  if (links.length <= SLOTS) {
    const slices = links.map(slice);
    return { slices, colorOf: new Map(links.map((k, i) => [k.id, `var(--sv-${i + 1})`])) };
  }
  const sorted = [...links].sort((a, b) => b.allocation_amount_cents - a.allocation_amount_cents);
  const top = sorted.slice(0, SLOTS - 1);
  const rest = sorted.slice(SLOTS - 1);
  const slices = [...top.map(slice), { key: 'other', color: 'var(--sv-other)', cents: rest.reduce((n, k) => n + k.allocation_amount_cents, 0), links: rest, other: true }];
  const colorOf = new Map([...top.map((k, i) => [k.id, `var(--sv-${i + 1})`]), ...rest.map((k) => [k.id, 'var(--sv-other)'])]);
  return { slices, colorOf };
}

/** The line's tasks as a ring: a slice each (2px apart), the empty part what no task has taken; the % allocated in the
    middle. `hot`: the slice whose task is pointed at (in the ring or the list); `onHot(key | null)`. */
export function AllocationDonut({ links, valueCents, hot = null, onHot, size = 148 }) {
  const { slices } = slicesOf(links);
  const allocated = links.reduce((n, k) => n + k.allocation_amount_cents, 0);
  const whole = Math.max(valueCents, allocated, 1);
  const r = 46;
  const C = 2 * Math.PI * r;
  const gap = slices.length > 1 || allocated < whole ? 2 : 0;
  const pct = valueCents > 0 ? Math.round((allocated / valueCents) * 100) : 0;
  const over = allocated > valueCents;
  let at = 0;
  return (
    <svg className="sov-viz" viewBox="0 0 120 120" width={size} height={size} role="img"
      aria-label={`${pct}% allocated: ${moneyOfCents(allocated)} of ${moneyOfCents(valueCents)}, across ${links.length} task${links.length === 1 ? '' : 's'}`}>
      <g transform="rotate(-90 60 60)" fill="none" strokeWidth="16">
        <circle cx="60" cy="60" r={r} stroke="var(--sv-track)" />
        {slices.map((sl) => {
          const len = Math.max(0.6, (C * sl.cents) / whole - gap);
          const el = (
            <circle key={sl.key} cx="60" cy="60" r={r} stroke={sl.color} strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-at}
              style={{ pointerEvents: 'stroke', cursor: 'default', opacity: hot && hot !== sl.key ? 0.35 : 1, transition: 'opacity .12s' }}
              onMouseEnter={() => onHot && onHot(sl.key)} onMouseLeave={() => onHot && onHot(null)}>
              <title>{sl.other ? `${sl.links.length} more tasks · ${moneyOfCents(sl.cents)}` : `${taskWords(sl.links[0])} · ${moneyOfCents(sl.cents)}`}</title>
            </circle>
          );
          at += (C * sl.cents) / whole;
          return el;
        })}
      </g>
      <text x="60" y="58" textAnchor="middle" style={{ fontSize: 18, fontWeight: 600, fill: over ? 'var(--danger)' : 'var(--text-1)' }}>{pct}%</text>
      <text x="60" y="74" textAnchor="middle" style={{ fontSize: 9.5, fill: 'var(--text-3)' }}>{over ? 'over-allocated' : 'allocated'}</text>
    </svg>
  );
}

/** A line's allocation at a glance, under it in the table: a segment a task (one hue, 2px apart), the track what's
    not linked; hover names it. */
export function AllocationBar({ links, valueCents }) {
  const [tip, tipOn] = useHoverTip({ maxWidth: 320 });
  const allocated = links.reduce((n, k) => n + k.allocation_amount_cents, 0);
  const whole = Math.max(valueCents, allocated);
  const left = valueCents - allocated;
  if (whole <= 0) return null;
  const parts = [
    ...links.map((k) => ({ key: String(k.id), cents: k.allocation_amount_cents, fill: allocated > valueCents ? 'var(--danger)' : 'var(--sv-1)',
      tip: `${taskWords(k)} · ${valueCents > 0 ? `${Math.round((k.allocation_amount_cents / valueCents) * 10000) / 100}% · ` : ''}${moneyOfCents(k.allocation_amount_cents)}` })),
    ...(left > 0 ? [{ key: 'left', cents: left, fill: 'var(--sv-track)', tip: `Not linked to a task yet · ${moneyOfCents(left)}` }] : []),
  ].filter((p) => p.cents > 0);
  return (
    <>
      <div className="sov-viz" aria-hidden="true" style={{ display: 'flex', gap: 2, height: 6, maxWidth: 380, marginTop: 6 }}>
        {parts.map((p, i) => (
          <span key={p.key} {...tipOn(p.tip)}
            style={{ flex: `${p.cents} 1 0`, minWidth: 3, background: p.fill,
              borderRadius: `${i === 0 ? 3 : 0}px ${i === parts.length - 1 ? 3 : 0}px ${i === parts.length - 1 ? 3 : 0}px ${i === 0 ? 3 : 0}px` }} />
        ))}
      </div>
      {tip}
    </>
  );
}
